import { SVButton, SVEmailInput, SVForm, SVTextInput } from '@/components/core';
import { FORM_FIELDS } from '@/constants/formFields';
import { MESSAGES } from '@/constants/messages';
import { REGEX } from '@/constants/regex';
import { UpdateEmailFields } from '@/types/common';
import showToast from '@/utils/toast';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

type ChangeEmailModal = {
  userType: 'Staff' | 'Customer' | 'Teammate';
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sendOtp: UseMutateAsyncFunction<unknown, Error, void, unknown>;
  changeEmail: UseMutateAsyncFunction<
    unknown,
    Error,
    UpdateEmailFields,
    unknown
  >;
  isChangeEmailLoading: boolean;
  scrollToTop: (behavior?: ScrollBehavior) => void;
};

export default function ChangeEmailModal({
  userType,
  open,
  setOpen,
  sendOtp,
  changeEmail,
  isChangeEmailLoading,
  scrollToTop,
}: ChangeEmailModal) {
  const formMethods = useForm<UpdateEmailFields>({
    defaultValues: {
      email: '',
      confirmEmail: '',
      otp: '',
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      sendOtp();
      formMethods.reset();
    }
  }, [open]);

  async function manageChangeEmail(data: UpdateEmailFields) {
    try {
      await changeEmail(data);
      showToast({
        type: 'success',
        data: { content: `${userType} email has been successfully changed` },
      });
      setOpen(false);
      scrollToTop();
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
      <SVForm<UpdateEmailFields>
        methods={formMethods}
        onSubmit={manageChangeEmail}>
        <DialogContent>
          <Typography variant="h6" component="h2" mb={2}>
            Change Email
          </Typography>
          <SVEmailInput name="email" {...FORM_FIELDS.EMAIL} />
          <SVEmailInput
            name="confirmEmail"
            validateMatch="email"
            {...FORM_FIELDS.CONFIRM_EMAIL}
          />
          <SVTextInput
            label="One Time Password"
            name="otp"
            rules={{
              required: true,
              pattern: {
                regex: REGEX.CHECK_NUMBER,
                readablePatternName: 'code',
              },
            }}
          />
          <SVButton variant="outlined" onClick={() => sendOtp()} type="button">
            Resend Code
          </SVButton>
        </DialogContent>
        <DialogActions>
          <SVButton onClick={() => setOpen(false)} variant="tertiary">
            Back
          </SVButton>
          <SVButton
            type="submit"
            isLoading={isChangeEmailLoading}
            onClick={formMethods.handleSubmit(manageChangeEmail)}>
            Change
          </SVButton>
        </DialogActions>
      </SVForm>
    </Dialog>
  );
}
